import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  TrendingUp,
  Music,
  Plus,
  Eye
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const { api } = useApi();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch recent orders (admin endpoint)
      const ordersResponse = await api.get('/admin/orders?limit=5');
      setRecentOrders(ordersResponse.data.data.orders || []);

      // Calculate basic stats from orders
      const orders = ordersResponse.data.data.orders || [];
      const totalOrders = orders.length;
      const pendingOrders = orders.filter(order => order.status === 'pending').length;
      const completedOrders = orders.filter(order => order.status === 'completed').length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.price || 0), 0);

      setStats({
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning-600 bg-warning-50';
      case 'processing':
        return 'text-primary-600 bg-primary-50';
      case 'completed':
        return 'text-success-600 bg-success-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <FileText className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner size="xl" text="Loading dashboard..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <img 
                src="/logo.png" 
                alt="BandCam.io Logo" 
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold">Welcome back, {user?.directorName}!</h1>
              <p className="text-primary-100">
                Manage your band's fundraising orders and track your progress.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-primary-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalOrders || 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-warning-100 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pendingOrders || 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-success-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.completedOrders || 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-secondary-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-secondary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(stats?.totalRevenue || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
              <Link
                to="/orders"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all
              </Link>
            </div>
            
            {recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No orders yet</p>
                <p className="text-sm text-gray-400">Orders will appear here once customers start placing them.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {order.recipientName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {order.occasion} • ${order.price}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/orders/${order.id}`}
                      className="text-primary-600 hover:text-primary-500"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link
                to="/profile"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="bg-primary-100 p-2 rounded-lg">
                  <Music className="w-5 h-5 text-primary-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Update Band Profile</p>
                  <p className="text-xs text-gray-500">Set your pricing and band information</p>
                </div>
              </Link>

              <Link
                to="/payments"
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="bg-success-100 p-2 rounded-lg">
                  <DollarSign className="w-5 h-5 text-success-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">View Payments</p>
                  <p className="text-xs text-gray-500">Check your earnings and payout history</p>
                </div>
              </Link>

              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="bg-secondary-100 p-2 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-secondary-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Share Your Link</p>
                  <p className="text-xs text-gray-500">Get more orders by sharing with families</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Reminder */}
        {!user?.pricePerVideo && (
          <div className="card bg-warning-50 border-warning-200">
            <div className="flex items-center">
              <div className="bg-warning-100 p-3 rounded-lg">
                <Plus className="w-6 h-6 text-warning-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-warning-800">
                  Complete Your Setup
                </h3>
                <p className="text-warning-700">
                  Set your video pricing to start accepting orders from families.
                </p>
              </div>
              <Link
                to="/profile"
                className="btn-warning"
              >
                Set Pricing
              </Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard; 